// ignore-string-externalization
import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { screenXsMax, screenSmMax, spacer16, spacer24, spacer40, cta1FontWeight, heading2FontSize, heading2LineHeightSmall, screenSmMin, screenMdMax, spacer8 } from '@spotify-internal/encore-web';
import { ContentGrid } from '../../ContentGrid';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { Headline, Supertext, Link, ImageAspectBox16x9 } from '../../ui';
import { transformScaleUp, withoutProps } from '../../utils';
import { hexToRgba } from '@mrkt/features/color-conversion';
import { MODULE_THEMES } from '../../Theme';
import { itemToSearchHit } from '../../Search';
import { useLocale } from '@mrkt/features/i18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var FeaturedContent = styled.div.withConfig({
  displayName: "FeaturedContentModule__FeaturedContent",
  componentId: "sc-15z3vhg-0"
})(["& > div > div,ul{padding-bottom:0;}& > div > div > ul > li{margin-bottom:", ";}& > div:first-child > div > p{font-size:", ";line-height:", ";font-weight:", ";letter-spacing:-1.28px;text-transform:none;padding-top:", ";}"], spacer24, heading2FontSize, heading2LineHeightSmall, cta1FontWeight, spacer8);
var Hero = styled.div.withConfig({
  displayName: "FeaturedContentModule__Hero",
  componentId: "sc-15z3vhg-1"
})(["display:flex;flex-direction:row;width:100%;padding:", " ", ";color:", ";background-color:", ";&:hover{p,h1{color:", ";}img{", ";transition:transform 0.3s ease-in-out;}}@media (max-width:", "){flex-direction:column;padding-left:", ";padding-right:", ";}"], spacer40, spacer40, function (_ref) {
  var color = _ref.color;
  return color;
}, function (_ref2) {
  var background = _ref2.background;
  return background;
}, function (props) {
  return hexToRgba(props.color, 0.75);
}, transformScaleUp, screenXsMax, spacer16, spacer16);
var StyledLink = styled(withoutProps(Link, 'textColor')).withConfig({
  displayName: "FeaturedContentModule__StyledLink",
  componentId: "sc-15z3vhg-2"
})(["color:", ";@media (max-width:", "){& h1{line-height:36px;}}&&:hover:not(:disabled),&&:focus:not(:disabled){color:", ";}&&:focus:not(:disabled) h1{outline:1px;outline-style:dotted;outline-color:black;}"], function (_ref3) {
  var textColor = _ref3.textColor;
  return textColor;
}, screenSmMax, function (props) {
  return hexToRgba(props.textColor || '', 0.75);
});
var ImageContainer = styled(ImageAspectBox16x9).withConfig({
  displayName: "FeaturedContentModule__ImageContainer",
  componentId: "sc-15z3vhg-3"
})(["height:100%;width:120%;order:3;overflow:hidden;@media (max-width:", "){width:100%;order:2;}"], screenXsMax);
var TextContainer = styled.div.withConfig({
  displayName: "FeaturedContentModule__TextContainer",
  componentId: "sc-15z3vhg-4"
})(["order:2;padding-right:", ";align-self:center;height:100%;width:80%;& h1{padding-bottom:0;}@media (max-width:", "){padding-top:", ";width:100%;order:3;}"], spacer40, screenXsMax, spacer16);
var StyledHeadline = styled(Headline).withConfig({
  displayName: "FeaturedContentModule__StyledHeadline",
  componentId: "sc-15z3vhg-5"
})(["@media (min-width:", ") and (max-width:", "){padding-bottom:", ";}"], screenSmMin, screenMdMax, spacer16);
var StyledSupertext = styled(Supertext).withConfig({
  displayName: "FeaturedContentModule__StyledSupertext",
  componentId: "sc-15z3vhg-6"
})(["padding-bottom:", ";"], spacer16);
var TRACK_CAT = 'module-featuredContent';
export var FeaturedContentModule = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var _ref4$color = _ref4.color,
      color = _ref4$color === void 0 ? '' : _ref4$color,
      _ref4$background = _ref4.background,
      background = _ref4$background === void 0 ? '' : _ref4$background,
      _ref4$caption = _ref4.caption,
      caption = _ref4$caption === void 0 ? '' : _ref4$caption,
      _ref4$items = _ref4.items,
      items = _ref4$items === void 0 ? [] : _ref4$items,
      _ref4$gridSupertext = _ref4.gridSupertext,
      gridSupertext = _ref4$gridSupertext === void 0 ? '' : _ref4$gridSupertext,
      _ref4$heroSupertext = _ref4.heroSupertext,
      heroSupertext = _ref4$heroSupertext === void 0 ? '' : _ref4$heroSupertext,
      _ref4$theme = _ref4.theme,
      theme = _ref4$theme === void 0 ? '' : _ref4$theme,
      articleLink = _ref4.articleLink;
  var locale = useLocale();
  var hero = itemToSearchHit(articleLink, locale);
  var validatedItems = items.filter(function (i) {
    return i === null || i === void 0 ? void 0 : i.contentType;
  }, true).slice(0, 3);
  return /*#__PURE__*/_jsxs(FeaturedContent, {
    children: [/*#__PURE__*/_jsx(ThemeProvider, {
      theme: MODULE_THEMES[theme],
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(ContentGrid, {
          ref: ref,
          trackCategory: TRACK_CAT,
          "data-testid": "featuredContent-module-".concat('id'),
          items: validatedItems,
          supertext: gridSupertext
        })
      })
    }), hero && /*#__PURE__*/_jsx(StyledLink, {
      textColor: color,
      href: hero.url,
      track: [TRACK_CAT, "CTA Link - ".concat(hero.title)],
      children: /*#__PURE__*/_jsxs(Hero, {
        color: color,
        background: background,
        children: [/*#__PURE__*/_jsxs(TextContainer, {
          children: [/*#__PURE__*/_jsx(StyledSupertext, {
            children: heroSupertext
          }), hero.title && /*#__PURE__*/_jsx(StyledHeadline, {
            children: hero.title
          })]
        }), /*#__PURE__*/_jsx(ImageContainer, {
          children: hero.image && /*#__PURE__*/_jsx(ContentfulImage, {
            asset: hero.image,
            caption: caption
          })
        })]
      })
    })]
  });
});